<!--
  -  Copyright (c) Qwoach LLC 2026. All rights reserved.
  -  Unauthorized copying of this file, via any medium is strictly prohibited
  -  Proprietary and confidential
  -->

<template>
	<div class="editor-color-menu-root">
		<el-popover
			v-model:visible="isPopoverVisible"
			placement="top-start"
			trigger="click"
			:width="460"
			:fallback-placements="['bottom-start']"
			popper-class="editor-color-menu-root__popover">
			<template #reference>
				<button
					class="menubar__button"
					:class="{ 'is-active': hasAnyColorFormattingComputed }"
					type="button"
					:title="triggerTitleComputed">
					<span
						class="editor-color-menu-root__trigger-letter"
						:style="{'border-bottom-color': currentTextColorComputed || defaultTextColorPreview}">
						A
					</span>
					<i class="fontello icon-angle-down editor-color-menu-root__trigger-arrow"/>
				</button>
			</template>

			<div class="editor-color-menu-root__content">
				<div class="row mx-0">
					<div
						v-if="isTextHighlightEnabledComputed"
						:class="sectionColumnClassComputed">
							<div class="editor-color-menu-root__section-title">Background color</div>
								<PaletteColorPicker
									inline
									:colors="editorColorPickerPalette"
									:colorSize="paletteColorSize"
									:shape="paletteColorShape"
									:rowLength="paletteColorRowLength"
									:swatchGap="paletteColorSwatchGap"
									:modelValue="currentTextHighlightColorComputed"
									@update:modelValue="setTextHighlight"/>
					</div>

					<div
						v-if="isTextColorEnabledComputed"
						:class="sectionColumnClassComputed">
							<div class="editor-color-menu-root__section-title">Text color</div>
								<PaletteColorPicker
									inline
									:colors="editorColorPickerPalette"
									:colorSize="paletteColorSize"
									:shape="paletteColorShape"
									:rowLength="paletteColorRowLength"
									:swatchGap="paletteColorSwatchGap"
									:modelValue="currentTextColorComputed"
									@update:modelValue="setTextColor"/>
					</div>
				</div>

				<div class="editor-color-menu-root__footer pt-2 mt-2">
					<qw-button
						class="editor-color-menu-root__clear-button"
						simple
						@click="clearTextStyles">
						Clear colors
					</qw-button>
				</div>
			</div>
		</el-popover>
	</div>
</template>

<script>
import BaseComponent from "@/core/components/BaseComponent.vue";
import {Editor} from "@tiptap/vue-3";
import PaletteColorPicker from "@/core/components/PaletteColorPicker.vue";
import {editorColorPickerPalette} from "@/core/js/PaletteEnum";

export default {
	name: "EditorColorMenu",
	components: {PaletteColorPicker},
	extends: BaseComponent,

	props: {
		editor: {Editor, required: true},
		toolbarOptions: {type: Object, default: () => ({})},
	},

	data() {
		return {
			isPopoverVisible: false,
			paletteColorSize: 18,
			paletteColorShape: "squares",
			paletteColorRowLength: 8,
			paletteColorSwatchGap: 1,
		}
	},

	computed: {
		editorColorPickerPalette() {
			return editorColorPickerPalette
		},
		defaultTextColorPreview() {
			return editorColorPickerPalette[0][0]
		},
		isTextColorEnabledComputed() {
			return this.toolbarOptions?.textColor === true
		},
		isTextHighlightEnabledComputed() {
			return this.toolbarOptions?.backgroundColor === true
		},
		currentTextColorComputed() {
			return this.editor.getAttributes('textStyle').color || null
		},
		currentTextHighlightColorComputed() {
			let color = this.editor.getAttributes('highlight').color
			if (color) {
				return color
			}
			return this.editor.isActive('highlight') ? 'yellow' : null
		},
		hasAnyColorFormattingComputed() {
			return this.selectionHasTextColor() || this.selectionHasHighlight()
		},
		triggerTitleComputed() {
			if (this.isTextColorEnabledComputed && this.isTextHighlightEnabledComputed) {
				return "Text and background color"
			}
			if (this.isTextHighlightEnabledComputed) {
				return "Background color"
			}
			return "Text color"
		},
		sectionColumnClassComputed() {
			if (this.isTextColorEnabledComputed && this.isTextHighlightEnabledComputed) {
				return 'col-6'
			}
			return 'col-12'
		},
	},

	methods: {
		setTextColor(color) {
			this.$analyticManager.trackEvent("Text color clicked", this.analyticsPropsMethod())
			this.editor.chain().focus().setColor(color).run()
			this.isPopoverVisible = false
		},

		setTextHighlight(color) {
			this.$analyticManager.trackEvent("Text highlight clicked", this.analyticsPropsMethod())
			this.editor.chain().focus().setHighlight({color}).run()
			this.isPopoverVisible = false
		},

		clearTextStyles() {
			this.$analyticManager.trackEvent("Text colors cleared", this.analyticsPropsMethod())
			this.editor.chain().focus().unsetColor().unsetHighlight().run()
			this.isPopoverVisible = false
		},

		selectionHasTextColor() {
			let currentColor = this.currentTextColorComputed
			if (currentColor) {
				return true
			}
			return this.selectionHasMark(mark => mark.type.name === 'textStyle' && !!mark.attrs.color)
		},

		selectionHasHighlight() {
			if (this.editor.isActive('highlight')) {
				return true
			}
			return this.selectionHasMark(mark => mark.type.name === 'highlight')
		},

		selectionHasMark(predicate) {
			let {selection, doc} = this.editor.state
			let {from, to, empty} = selection
			let hasMark = false
			if (empty) {
				return false
			}
			doc.nodesBetween(from, to, node => {
				if (hasMark || !node.isText) {
					return
				}
				hasMark = node.marks.some(predicate)
			})
			return hasMark
		},
	},
}
</script>

<style lang="scss">
.editor-color-menu-root {
	display: inline-flex;

	&__trigger-letter {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 14px;
		font-weight: bold;
		line-height: 1;
		border-bottom: 2px solid transparent;
	}

	&__trigger-arrow {
		font-size: 10px;
		margin-left: 4px;
	}

	&__content {
		min-width: 392px;
	}

	&__section-title {
		font-size: 13px;
		font-weight: 600;
		margin-bottom: 8px;
	}

	&__footer {
		border-top: 1px solid rgba(0, 0, 0, 0.08);
		display: flex;
		justify-content: flex-end;
	}

	&__clear-button {
		font-size: 13px;
	}

}

.editor-color-menu-root__popover {
	.palette-color-picker-root {
		.vue-swatches__row:nth-child(2) {
			margin-bottom: 10px;
		}
	}
}
</style>
